import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "@/hooks/use-toast";

interface WishlistItem {
  id: string;
  product_id: string;
  product: {
    id: string;
    name: string;
    slug: string;
    price: number;
    original_price: number | null;
    images: string[];
    category: { name: string } | null;
  };
}

interface WishlistContextType {
  items: WishlistItem[];
  loading: boolean;
  isInWishlist: (productId: string) => boolean;
  addToWishlist: (productId: string) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  toggleWishlist: (productId: string) => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchWishlist = async () => {
    if (!user) {
      setItems([]);
      return;
    }

    setLoading(true);
    
    // First get wishlist items
    const { data: wishlistData, error: wishlistError } = await supabase
      .from("wishlist")
      .select("id, product_id")
      .eq("user_id", user.id);

    if (wishlistError) {
      console.error("Error fetching wishlist:", wishlistError);
      setLoading(false);
      return;
    }

    if (!wishlistData || wishlistData.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    // Then fetch the product details
    const productIds = wishlistData.map(item => item.product_id);
    const { data: products, error: productsError } = await supabase
      .from("products")
      .select("id, name, slug, price, original_price, images, category_id")
      .in("id", productIds);

    if (productsError) {
      console.error("Error fetching products:", productsError);
      setLoading(false);
      return;
    }

    // Get category names if needed
    const categoryIds = products?.map(p => p.category_id).filter(Boolean) || [];
    let categoriesMap: Record<string, { name: string }> = {};
    
    if (categoryIds.length > 0) {
      const { data: categories } = await supabase
        .from("categories")
        .select("id, name")
        .in("id", categoryIds);
      
      if (categories) {
        categoriesMap = categories.reduce((acc, cat) => {
          acc[cat.id] = { name: cat.name };
          return acc;
        }, {} as Record<string, { name: string }>);
      }
    }

    // Combine wishlist items with product details
    const itemsWithProducts: WishlistItem[] = wishlistData
      .map(wishlistItem => {
        const product = products?.find(p => p.id === wishlistItem.product_id);
        if (!product) return null;
        return {
          id: wishlistItem.id,
          product_id: wishlistItem.product_id,
          product: {
            id: product.id,
            name: product.name,
            slug: product.slug,
            price: product.price,
            original_price: product.original_price,
            images: product.images || [],
            category: product.category_id ? categoriesMap[product.category_id] || null : null,
          },
        };
      })
      .filter((item): item is WishlistItem => item !== null);

    setItems(itemsWithProducts);
    setLoading(false);
  };

  useEffect(() => {
    fetchWishlist();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isInWishlist = (productId: string) => {
    return items.some((item) => item.product_id === productId);
  };

  const addToWishlist = async (productId: string) => {
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to add items to wishlist",
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase.from("wishlist").insert({
      user_id: user.id,
      product_id: productId,
    });

    if (error) {
      if (error.code === "23505") {
        toast({ title: "Already in wishlist", description: "This item is already in your wishlist" });
      } else {
        toast({ title: "Error", description: "Failed to add to wishlist", variant: "destructive" });
      }
    } else {
      toast({ title: "Added to wishlist", description: "Item saved to your wishlist" });
      fetchWishlist();
    }
  };

  const removeFromWishlist = async (productId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from("wishlist")
      .delete()
      .eq("user_id", user.id)
      .eq("product_id", productId);

    if (error) {
      toast({ title: "Error", description: "Failed to remove from wishlist", variant: "destructive" });
    } else {
      toast({ title: "Removed", description: "Item removed from wishlist" });
      fetchWishlist();
    }
  };

  const toggleWishlist = async (productId: string) => {
    if (isInWishlist(productId)) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(productId);
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        items,
        loading,
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
}
